// Vistas del módulo quotes — una por tab del registry.tsx:
//   /m/quotes/list      → lista + drawer de detalle
//   /m/quotes/create    → formulario
//   /m/quotes/templates → gestión de plantillas reutilizables

import { useState } from 'react';

import { QuoteDetail } from './components/QuoteDetail';
import { QuoteForm } from './components/QuoteForm';
import { QuoteList } from './components/QuoteList';
import { useQuoteTemplates } from './hooks/useQuotes';
import type { Quote, QuoteStatus } from './types';

const STATUS_OPTIONS: Array<{ value: '' | QuoteStatus; label: string }> = [
  { value: '', label: 'Todas' },
  { value: 'draft', label: 'Borrador' },
  { value: 'sent', label: 'Enviada' },
  { value: 'viewed', label: 'Vista' },
  { value: 'accepted', label: 'Aceptada' },
  { value: 'rejected', label: 'Rechazada' },
  { value: 'expired', label: 'Expirada' },
];

export function QuotesListView() {
  const [statusFilter, setStatusFilter] = useState<'' | QuoteStatus>('');
  const [selected, setSelected] = useState<Quote | null>(null);

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Cotizaciones</h1>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as '' | QuoteStatus)}
          className="border rounded px-2 py-1 text-sm"
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      <QuoteList
        status={statusFilter || undefined}
        onSelect={(q) => setSelected(q)}
      />

      {selected && (
        <div className="mt-4 border-t pt-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-base font-semibold">Detalle</h2>
            <button
              type="button"
              onClick={() => setSelected(null)}
              className="text-xs text-muted-foreground"
            >
              cerrar
            </button>
          </div>
          <QuoteDetail
            quote={selected}
            onUpdated={(updated) => setSelected(updated)}
          />
        </div>
      )}
    </div>
  );
}

export function QuotesCreateView() {
  const [refreshKey, setRefreshKey] = useState(0);
  return (
    <div className="space-y-4 p-4">
      <h1 className="text-xl font-semibold">Crear cotización</h1>
      <QuoteForm
        key={refreshKey}
        onCreated={() => setRefreshKey((k) => k + 1)}
      />
    </div>
  );
}

export function QuotesTemplatesView() {
  const { data, loading, error } = useQuoteTemplates();
  if (loading) return <div className="p-6 text-sm">Cargando plantillas…</div>;
  if (error) return <div className="p-6 text-sm text-destructive">Error: {error}</div>;
  return (
    <div className="space-y-4 p-4">
      <h1 className="text-xl font-semibold">Plantillas de cotización</h1>
      {data.length === 0 ? (
        <div className="text-sm text-muted-foreground">
          Sin plantillas todavía. Crea una desde la API o el admin de Django
          para acelerar la creación de cotizaciones recurrentes.
        </div>
      ) : (
        <ul className="space-y-2">
          {data.map((t) => (
            <li key={t.id} className="border rounded p-3">
              <div className="font-medium">{t.name}</div>
              <div className="text-xs text-muted-foreground">
                {t.line_items.length} líneas · {t.currency}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
